import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginRequest, RegisterRequest, VerifyEmailRequest } from '@/types/auth'
import { authAPI } from '@/services/api'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isEmailVerified = computed(() => user.value?.is_email_verified ?? false)

  // Actions
  const setTokens = (access: string, refresh: string) => {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
  }

  const clearTokens = () => {
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  const setUser = (userData: User) => {
    user.value = userData
  }

  const clearUser = () => {
    user.value = null
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  // Auth actions
  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await authAPI.login(credentials)
      setTokens(response.access_token, response.refresh_token)
      setUser(response.user)
      
      return response
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Login failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData: RegisterRequest) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await authAPI.register(userData)
      return response
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Registration failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const verifyEmail = async (verificationData: VerifyEmailRequest) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await authAPI.verifyEmail(verificationData)
      setTokens(response.access_token, response.refresh_token)
      setUser(response.user)
      
      // Return response with group_id if user joined through invite
      return response
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Email verification failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const fetchCurrentUser = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const userData = await authAPI.getCurrentUser()
      setUser(userData)
      
      return userData
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to fetch user'
      setError(errorMessage)
      clearTokens()
      clearUser()
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      if (accessToken.value) {
        await authAPI.logout()
      }
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearTokens()
      clearUser()
      setError(null)
    }
  }

  const refreshTokenAction = async () => {
    try {
      if (!refreshToken.value) {
        throw new Error('No refresh token available')
      }

      const response = await authAPI.refreshToken({ refresh_token: refreshToken.value })
      setTokens(response.access_token, response.refresh_token)
      setUser(response.user)
      
      return response
    } catch (err: any) {
      clearTokens()
      clearUser()
      throw new Error('Token refresh failed')
    }
  }

  // Initialize auth state on app start
  const initializeAuth = async () => {
    if (accessToken.value && !user.value) {
      try {
        await fetchCurrentUser()
      return true
      } catch (err) {
        clearTokens()
        return false
      }
    }
    return !!accessToken.value
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    isEmailVerified,

    // Actions
    login,
    register,
    verifyEmail,
    fetchCurrentUser,
    logout,
    refreshTokenAction,
    initializeAuth,
    setUser,
    setError,
    setLoading
  }
})
